/**
 * StepActions Component - Step transition controls
 *
 * Provides buttons for:
 * - Approving the current step (advance to next)
 * - Skipping the current step (with confirmation dialog)
 * - Revisiting previous steps (with option to clear subsequent data)
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  SkipNext as SkipIcon,
  History as RevisitIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  useWorkflow,
  WORKFLOW_STEPS,
  STEP_LABELS,
  type WorkflowStep,
} from '../../contexts/WorkflowContext';

const StepActions: React.FC = () => {
  const {
    currentStep,
    stepStatuses,
    isLoading,
    error,
    workflowComplete,
    approveCurrentStep,
    skipCurrentStep,
    revisitStep,
    canApprove,
    canSkip,
    canRevisit,
    clearError,
  } = useWorkflow();

  // Dialog states
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [skipReason, setSkipReason] = useState('');
  const [revisitDialogOpen, setRevisitDialogOpen] = useState(false);
  const [revisitTarget, setRevisitTarget] = useState<WorkflowStep | null>(null);
  const [clearSubsequent, setClearSubsequent] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Revisit menu
  const [revisitAnchorEl, setRevisitAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const revisitMenuOpen = Boolean(revisitAnchorEl);

  // Get steps that can be revisited
  const revisitableSteps = WORKFLOW_STEPS.filter((step) => canRevisit(step));

  // Handle approve
  const handleApprove = async () => {
    const response = await approveCurrentStep();
    if (response) {
      setSuccessMessage(response.message);
      setTimeout(() => setSuccessMessage(null), 3000);
    }
  };

  // Handle skip dialog
  const handleOpenSkipDialog = () => {
    setSkipDialogOpen(true);
    setSkipReason('');
  };

  const handleCloseSkipDialog = () => {
    setSkipDialogOpen(false);
    setSkipReason('');
  };

  const handleConfirmSkip = async () => {
    const response = await skipCurrentStep(skipReason);
    if (response) {
      setSuccessMessage(response.message);
      setTimeout(() => setSuccessMessage(null), 3000);
    }
    handleCloseSkipDialog();
  };

  // Handle revisit menu
  const handleOpenRevisitMenu = (event: React.MouseEvent<HTMLElement>) => {
    setRevisitAnchorEl(event.currentTarget);
  };

  const handleCloseRevisitMenu = () => {
    setRevisitAnchorEl(null);
  };

  const handleSelectRevisitStep = (step: WorkflowStep) => {
    setRevisitTarget(step);
    setClearSubsequent(false);
    setRevisitDialogOpen(true);
    handleCloseRevisitMenu();
  };

  // Handle revisit dialog
  const handleCloseRevisitDialog = () => {
    setRevisitDialogOpen(false);
    setRevisitTarget(null);
    setClearSubsequent(false);
  };

  const handleConfirmRevisit = async () => {
    if (!revisitTarget) return;

    const response = await revisitStep(revisitTarget, clearSubsequent);
    if (response) {
      setSuccessMessage(response.message);
      setTimeout(() => setSuccessMessage(null), 3000);
    }
    handleCloseRevisitDialog();
  };

  // Show completion message if workflow is done
  if (workflowComplete) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Alert severity="success" sx={{ flexGrow: 1 }}>
          Workflow complete! All steps have been finished.
        </Alert>
        {revisitableSteps.length > 0 && (
          <>
            <Button
              variant="outlined"
              startIcon={<RevisitIcon />}
              onClick={handleOpenRevisitMenu}
            >
              Revisit Step
            </Button>
            <Menu
              anchorEl={revisitAnchorEl}
              open={revisitMenuOpen}
              onClose={handleCloseRevisitMenu}
            >
              {revisitableSteps.map((step) => (
                <MenuItem
                  key={step}
                  onClick={() => handleSelectRevisitStep(step)}
                >
                  <ListItemIcon>
                    {stepStatuses[step] === 'approved' ? (
                      <ApproveIcon color="success" fontSize="small" />
                    ) : stepStatuses[step] === 'skipped' ? (
                      <SkipIcon color="warning" fontSize="small" />
                    ) : null}
                  </ListItemIcon>
                  <ListItemText>{STEP_LABELS[step]}</ListItemText>
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Error/Success messages */}
      {error && (
        <Alert severity="error" onClose={clearError}>
          {error}
        </Alert>
      )}
      {successMessage && <Alert severity="success">{successMessage}</Alert>}

      {/* Action buttons */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        {/* Revisit button (only if there are previous steps) */}
        {revisitableSteps.length > 0 && (
          <>
            <Tooltip title="Go back to a previous step">
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<RevisitIcon />}
                onClick={handleOpenRevisitMenu}
                disabled={isLoading}
              >
                Revisit
              </Button>
            </Tooltip>
            <Menu
              anchorEl={revisitAnchorEl}
              open={revisitMenuOpen}
              onClose={handleCloseRevisitMenu}
            >
              {revisitableSteps.map((step) => (
                <MenuItem
                  key={step}
                  onClick={() => handleSelectRevisitStep(step)}
                >
                  <ListItemIcon>
                    {stepStatuses[step] === 'approved' ? (
                      <ApproveIcon color="success" fontSize="small" />
                    ) : stepStatuses[step] === 'skipped' ? (
                      <SkipIcon color="warning" fontSize="small" />
                    ) : null}
                  </ListItemIcon>
                  <ListItemText>{STEP_LABELS[step]}</ListItemText>
                </MenuItem>
              ))}
            </Menu>
          </>
        )}

        <Divider orientation="vertical" flexItem />

        {/* Skip button */}
        <Tooltip title="Skip this step (may affect later steps)">
          <span>
            <Button
              variant="outlined"
              color="warning"
              startIcon={
                isLoading ? <CircularProgress size={20} /> : <SkipIcon />
              }
              onClick={handleOpenSkipDialog}
              disabled={!canSkip || isLoading}
            >
              Skip Step
            </Button>
          </span>
        </Tooltip>

        {/* Approve button */}
        <Tooltip title="Approve this step and continue">
          <span>
            <Button
              variant="contained"
              color="success"
              startIcon={
                isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <ApproveIcon />
                )
              }
              onClick={handleApprove}
              disabled={!canApprove || isLoading}
            >
              Approve & Continue
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Skip Confirmation Dialog */}
      <Dialog open={skipDialogOpen} onClose={handleCloseSkipDialog}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Skip {STEP_LABELS[currentStep]} Step?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Skipping this step may result in incomplete context for later steps.
            The agent may not have all the information it needs to provide the
            best recommendations.
          </DialogContentText>
          <TextField
            autoFocus
            label="Reason for skipping (optional)"
            fullWidth
            multiline
            rows={2}
            value={skipReason}
            onChange={(e) => setSkipReason(e.target.value)}
            placeholder="e.g., Already have requirements documented elsewhere"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSkipDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmSkip}
            color="warning"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={20} /> : 'Skip Step'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revisit Confirmation Dialog */}
      <Dialog open={revisitDialogOpen} onClose={handleCloseRevisitDialog}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RevisitIcon color="secondary" />
          Revisit {revisitTarget ? STEP_LABELS[revisitTarget] : ''} Step?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            You are about to go back to the{' '}
            {revisitTarget ? STEP_LABELS[revisitTarget] : ''} step. You can
            choose to keep or clear the data from steps that come after it.
          </DialogContentText>
          <FormControlLabel
            control={
              <Checkbox
                checked={clearSubsequent}
                onChange={(e) => setClearSubsequent(e.target.checked)}
              />
            }
            label="Clear data from later steps (they will need to be redone)"
          />
          {!clearSubsequent && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Later steps will keep their existing data, but it may become
              outdated if you make significant changes.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRevisitDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmRevisit}
            color="secondary"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={20} /> : 'Revisit Step'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StepActions;
